/*--------------------------------------------------------------
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * See License.txt in the project root for license information.
 *--------------------------------------------------------------*/

import { getPayloadObject, getTelemetryAttributes, ITelemetryContent, TelemetryConstant } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import { AdminRequestStatementButton } from './account-actions';
import { RequestStatementModal } from './account-statement-request-modal';

export * from './account-actions';
export * from './account-statement-request-modal';

interface IAccountLabel {
    name: string;
    accountNumber: string;
    createdOn: string;
    customerSincePrefix: string;
}

interface IAccountValues {
    availableCredit: string;
    availableCreditLabel: string;
}

/**
 * Prop for Available credit component.
 */
interface ICreditSummaryAvailableCredit {
    availableCreditLabel: string;
    availableCredit: string;
}

/**
 * Prop for Balanced owed component.
 */
interface ICreditSummaryBalanceOwed {
    balanceOwedLabel: string;
    balanceOwed: string;
}

/**
 * Prop for credit excess component.
 */
interface ICreditSummaryCreditExcess {
    creditExcessLabel: string | null;
    creditExcess: string | null;
}

interface ICreditSummaryLabel {
    availableCredit: string;
    balanceOwed: string;
    creditExcess: string | null;
}

interface ICreditSummaryValues {
    availableCredit: string;
    balanceOwed: string;
    creditExcess: string | null;
}

interface IBackButton {
    link: string;
    label: string;
    telemetryContent?: ITelemetryContent;
}

export const AccountLabels: React.FC<IAccountLabel> = ({ name, accountNumber, customerSincePrefix, createdOn }) => (
    <div className='ms-account-customer-balance__account__details'>
        <h4>
            {name}
        </h4>
        <div className='ms-account-customer-balance__account-number'>
            #
            {accountNumber}
        </div>
        <div className='ms-account-customer-balance__created-on'>
            {customerSincePrefix}
            {' '}
            {createdOn}
        </div>
    </div>
);

export const AccountValues: React.FC<IAccountValues> = ({ availableCredit, availableCreditLabel }) => (
    <div className='ms-account-customer-balance__account__credit'>
        <div className='ms-account-customer-balance__available-credit-label'>
            {availableCreditLabel}
        </div>
        <div className='ms-account-customer-balance__available-credit-amount'>
            {availableCredit}
        </div>
    </div>
);

export const CreditSummaryLabels: React.FC<ICreditSummaryLabel> = ({ availableCredit, balanceOwed, creditExcess }) => (
    <div className='ms-account-customer-balance__credit-summary__labels'>
        <div className='ms-account-customer-balance__available-credit-label'>
            {availableCredit}
        </div>
        <div className='ms-account-customer-balance__balance-owed-label'>
            {balanceOwed}
        </div>
        <div className='ms-account-customer-balance__credit-excess-label'>
            {creditExcess}
        </div>
    </div>
);

export const CreditSummaryValues: React.FC<ICreditSummaryValues> = ({ availableCredit, balanceOwed, creditExcess }) => (
    <div className='ms-account-customer-balance__credit-summary__values'>
        <div className='ms-account-customer-balance__available-credit-label'>
            {availableCredit}
        </div>
        <div className='ms-account-customer-balance__balance-owed-label'>
            {balanceOwed}
        </div>
        {creditExcess && <div className='ms-account-customer-balance__excess-credit-label'>
            {creditExcess}
        </div>}
    </div>
);

/**
 * Available credit component.
 * @param props - ICreditSummaryAvailableCredit.
 * @param props.availableCreditLabel - Available credit label.
 * @param props.availableCredit - Available credit value.
 * @returns - React Node for available credit component.
 */
export const AvailableCreditComponent: React.FC<ICreditSummaryAvailableCredit> = ({ availableCreditLabel, availableCredit }) => (
    <div className='ms-account-customer-balance__credit-summary__wrapper'>
        <div className='ms-account-customer-balance__credit-summary__labels'>
            <div className='ms-account-customer-balance__available-credit-label'>
                {availableCreditLabel}
            </div>
        </div>
        <div className='ms-account-customer-balance__credit-summary__values'>
            <div className='ms-account-customer-balance__available-credit-label'>
                {availableCredit}
            </div>
        </div>
    </div>
);

/**
 * Balance Owed Component.
 * @param props - ICreditSummaryBalanceOwed.
 * @param props.balanceOwedLabel - Balance Owed label.
 * @param props.balanceOwed - Balance Owed value.
 * @returns - React Node for balance owed component.
 */
export const BalanceOwedComponent: React.FC<ICreditSummaryBalanceOwed> = ({ balanceOwedLabel, balanceOwed }) => (
    <div className='ms-account-customer-balance__credit-summary__wrapper'>
        <div className='ms-account-customer-balance__credit-summary__labels'>
            <div className='ms-account-customer-balance__balance-owed-label'>
                {balanceOwedLabel}
            </div>
        </div>
        <div className='ms-account-customer-balance__credit-summary__values'>
            <div className='ms-account-customer-balance__balance-owed-label'>
                {balanceOwed}
            </div>
        </div>
    </div>
);

/**
 * Credit Excess Component.
 * @param props - ICreditSummaryCreditExcess.
 * @param props.creditExcessLabel - Credit excess label.
 * @param props.creditExcess - Credit excess value.
 * @returns - React Node for credit excess component.
 */
export const CreditExcessComponent: React.FC<ICreditSummaryCreditExcess> = ({ creditExcessLabel, creditExcess }) => (
    <div className='ms-account-customer-balance__credit-summary__wrapper'>
        {creditExcess && <div className='ms-account-customer-balance__credit-summary__labels'>
            <div className='ms-account-customer-balance__credit-excess-label'>
                {creditExcessLabel}
            </div>
        </div>}
        <div className='ms-account-customer-balance__credit-summary__values'>
            {creditExcess && <div className='ms-account-customer-balance__excess-credit-label'>
                {creditExcess}
            </div>}
        </div>
    </div>
);
export const BackButton: React.FC<IBackButton> = ({ link, label, telemetryContent }) => {
    const payLoad = getPayloadObject('click', telemetryContent!, TelemetryConstant.BackToShopping);
    const attributes = getTelemetryAttributes(telemetryContent!, payLoad);
    return (
        <a className='ms-account-loyalty__home-link msc-btn' href={link} {...attributes}>
            {label}
        </a>
    );
};

export {
    AdminRequestStatementButton,
    RequestStatementModal
};
